import { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { PersonStanding, Video, Zap, Volume2, VolumeX } from "lucide-react";
import Lenis from "lenis";
import { useLang } from "../store";
import { StudioCanvas, HOTSPOTS } from "../three/Studio";
import { useStudioAudio } from "../hooks/useStudioAudio";
import Marquee from "../components/Marquee";
import { FilmTicket } from "../components/FilmTicket";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";

const TEASERS = [
  { id: "equipment", route: "/noleggio-attrezzatura", unit: "CAMERA DEPT", film: "Nessun domani", year: 2025, seat: "A7", tilt: -2.4 },
  { id: "actors", route: "/filmografia?ruolo=attore", unit: "CASTING", film: "Il racconto dei racconti", year: 2015, seat: "B3", tilt: 1.8 },
  { id: "director", route: "/filmografia?ruolo=regista", unit: "REGIA", film: "L'equilibrista con la stella", year: 2021, seat: "C1", tilt: -1.1 },
  { id: "costumes", route: "/noleggio-costumi", unit: "WARDROBE", film: "Zoolander 2", year: 2016, seat: "D9", tilt: 2.3 },
  { id: "gallery", route: "/galleria", unit: "STILLS", film: "Origami", year: 2025, seat: "E4", tilt: -1.9 },
  { id: "stunt", route: "/stunt", unit: "STUNT DEPT", film: "Rewind", year: 2021, seat: "F6", tilt: 1.2 },
  { id: "production", route: "/produzioni", unit: "PRODUZIONE", film: "Tre storie straordinarie", year: 2023, seat: "G2", tilt: -2.8 },
  { id: "about", route: "/chi-sono", unit: "PRESS", film: "Ritratto di un'ossessione", year: 2021, seat: "H8", tilt: 1.6 },
  { id: "cinema", route: "/proiezione", unit: "SALA PROIEZIONE", film: "The Prisoner's Journey", year: 2024, seat: "I5", tilt: -0.9 },
  { id: "contacts", route: "/contatti", unit: "PRODUCTION OFFICE", film: "Nessun domani", year: 2025, seat: "L1", tilt: 2.0 },
];

function Intro({ onEnter }) {
  const { t } = useLang();
  return (
    <motion.div
      className="absolute inset-0 z-20 flex items-center justify-center bg-gradient-to-b from-black/70 via-black/40 to-black/80"
      exit={{ opacity: 0, transition: { duration: 0.8 } }}
      data-testid="studio-intro"
    >
      <div className="px-6 text-center">
        <motion.p
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="font-mono2 mb-6 text-[11px] tracking-[0.4em] text-amber-500"
        >
          {t("hero.kicker")}
        </motion.p>
        <h1 className="font-display text-5xl uppercase leading-[0.92] tracking-tight sm:text-7xl lg:text-8xl">
          <span className="mask-line"><motion.span initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}>{t("hero.line1")}</motion.span></span>
          <span className="mask-line"><motion.span initial={{ y: "115%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.62, ease: [0.16, 1, 0.3, 1] }} className="text-amber-500">{t("hero.line2")}</motion.span></span>
        </h1>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}
          className="font-serif-it mx-auto mt-6 max-w-xl text-lg italic text-slate-300"
        >
          {t("hero.sub")}
        </motion.p>
        <motion.button
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3 }}
          onClick={onEnter}
          data-testid="enter-studio-button"
          className="font-display mt-9 rounded-full bg-amber-600 px-9 py-4 text-sm tracking-[0.25em] text-black uppercase transition-all hover:bg-amber-500 hover:shadow-[0_0_50px_rgba(217,119,6,0.5)]"
        >
          {t("hero.cta")}
        </motion.button>
      </div>
    </motion.div>
  );
}

export default function Home() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [entered, setEntered] = useState(false);
  const [mode, setMode] = useState("walk");
  const [activeId, setActiveId] = useState(null);
  const [warpOpen, setWarpOpen] = useState(false);
  const [audioOn, setAudioOn] = useState(true);
  const lenisRef = useRef(null);
  useStudioAudio(entered && audioOn);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const onActive = useCallback((id) => setActiveId((prev) => (prev === id ? prev : id)), []);
  const onEnter = useCallback((id) => {
    const h = HOTSPOTS.find((s) => s.id === id);
    if (h) navigate(h.route);
  }, [navigate]);

  const active = HOTSPOTS.find((h) => h.id === activeId);

  return (
    <div className="grain">
      <section className="relative h-[100svh] w-full overflow-hidden" data-testid="studio-hero">
        <StudioCanvas mode={mode} activeId={activeId} onActive={onActive} audioOn={audioOn} />

        <AnimatePresence>{!entered && <Intro onEnter={() => setEntered(true)} />}</AnimatePresence>

        {entered && (
          <>
            <div className="absolute bottom-6 left-6 z-10 flex gap-2" data-testid="studio-mode-toggle">
              <button
                onClick={() => setMode("walk")}
                data-testid="studio-mode-walk"
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold tracking-wide backdrop-blur transition-colors ${mode === "walk" ? "border-amber-500 bg-amber-600/20 text-amber-400" : "border-white/15 bg-black/60 text-slate-300 hover:border-white/40"}`}
              >
                <PersonStanding size={14} /> {t("hero.modeWalk")}
              </button>
              <button
                onClick={() => setAudioOn(!audioOn)}
                data-testid="audio-toggle"
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold tracking-wide backdrop-blur transition-colors ${audioOn ? "border-amber-500 bg-amber-600/20 text-amber-400" : "border-white/15 bg-black/60 text-slate-300 hover:border-white/40"}`}
              >
                {audioOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
              </button>
              <button
                onClick={() => setMode("crane")}
                data-testid="studio-mode-orbit"
                className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold tracking-wide backdrop-blur transition-colors ${mode === "crane" ? "border-amber-500 bg-amber-600/20 text-amber-400" : "border-white/15 bg-black/60 text-slate-300 hover:border-white/40"}`}
              >
                <Video size={14} /> {t("hero.modeOrbit")}
              </button>
            </div>

            <div className="absolute bottom-6 right-6 z-10">
              <AnimatePresence>
                {warpOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                    className="mb-3 grid grid-cols-1 gap-1 rounded-lg border border-white/10 bg-black/85 p-2 backdrop-blur-xl"
                    data-testid="warp-menu"
                  >
                    {HOTSPOTS.map((h) => (
                      <button
                        key={h.id}
                        onClick={() => { onEnter(h.id); setWarpOpen(false); }}
                        data-testid={`warp-${h.id}`}
                        className="rounded px-3 py-1.5 text-left text-xs text-slate-300 transition-colors hover:bg-amber-600/20 hover:text-amber-400"
                      >
                        {t(`hotspots.${h.id}`)}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
              <button
                onClick={() => setWarpOpen(!warpOpen)}
                data-testid="warp-toggle"
                className="flex items-center gap-2 rounded-full border border-white/15 bg-black/60 px-4 py-2 text-xs font-semibold tracking-wide text-slate-300 backdrop-blur transition-colors hover:border-amber-500 hover:text-amber-400"
              >
                <Zap size={14} /> {t("hero.warp")}
              </button>
            </div>

            <div className="pointer-events-none absolute left-1/2 top-20 z-10 -translate-x-1/2">
              <p className="rounded-full bg-black/50 px-4 py-1.5 text-[11px] tracking-wide text-slate-400 backdrop-blur" data-testid="studio-hint">
                {mode === "walk" ? t("hero.hintWalk") : t("hero.hintOrbit")}
              </p>
            </div>

            <AnimatePresence>
              {active && (
                <motion.div
                  key={active.id}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
                  className="absolute bottom-20 left-1/2 z-10 -translate-x-1/2"
                  data-testid="hotspot-card"
                >
                  <button
                    onClick={() => onEnter(active.id)}
                    data-testid="hotspot-card-button"
                    className="font-display rounded border border-amber-500/70 bg-black/85 px-6 py-3 text-sm tracking-[0.2em] text-amber-400 uppercase shadow-[0_0_40px_rgba(217,119,6,0.35)] backdrop-blur transition-transform hover:scale-105"
                  >
                    {t(`hotspots.${active.id}`)} →
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

      </section>

      <Marquee />

      <section className="mx-auto max-w-[1600px] px-6 py-20" data-testid="section-teasers">
        <Reveal>
          <p className="font-mono2 text-[11px] tracking-[0.35em] text-amber-500">STAGE 7 — MAPPA DEL SET</p>
          <h2 className="font-display mt-3 text-3xl uppercase tracking-tight sm:text-4xl">{t("hero.legend")}</h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-7 sm:grid-cols-2 sm:gap-8">
          {TEASERS.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.06}>
              <FilmTicket
                id={s.id}
                index={i}
                film={s.film}
                year={s.year}
                unit={s.unit}
                seat={s.seat}
                tilt={s.tilt}
                section={t(`hotspots.${s.id}`)}
                onClick={() => navigate(s.route)}
              />
            </Reveal>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
